import { stacks } from "@dropbox/api-v2-client";
import { Avatar } from "@dropbox/dig-components/avatar";
import { IconButton, StylelessButton } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { SettingsLine } from "@dropbox/dig-icons/dist/mjs/assets";
import { useStackPageAugustRevisionEnabled } from "@mirage/august-revision-hook";
import { DashFacepile } from "@mirage/mosaics/DCLWrappers/DashFacepile";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { useStackChat } from "@mirage/service-experimentation/useStackChat";
import { getInitials } from "@mirage/shared/account";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import { membersFromStack } from "@mirage/stacks/Helpers/Utils";
import { memberTextForStack } from "@mirage/stacks/ShareModal/Utils";
import i18n from "@mirage/translations";
import classnames from "classnames";
import { useMemo } from "react";
import styles from "./SharingModuleAccessMembers.module.css";

type SharingModuleAccessMembersProps = {
  onShareSettingsClick: () => void;
  stack: stacks.Stack;
};

export function SharingModuleAccessMembers({
  onShareSettingsClick,
  stack,
}: SharingModuleAccessMembersProps) {
  const currentAccount = useDropboxAccount();
  const isStackChatEnabled = useStackChat();
  const augustRevisionEnabled = useStackPageAugustRevisionEnabled();
  const members = membersFromStack(stack, isStackChatEnabled);

  const isDashWelcomeStack =
    stack?.stack_data?.creation_type?.[".tag"] === "welcome_stack";

  const stackOwners =
    stack?.sharing_data?.members?.filter(
      (member) => member.permission?.[".tag"] === "owner",
    ) ?? [];
  const owner = stackOwners.length > 0 ? stackOwners[0] : undefined;

  const ownerDisplayName = useMemo(() => {
    if (owner) {
      return owner.subject?.[".tag"] === "user"
        ? owner.subject.display_name
        : currentAccount?.name.display_name;
    }
    return undefined;
  }, [owner, currentAccount?.name.display_name]);

  const ownerInitials = useMemo(() => {
    if (owner) {
      return getInitials(
        (owner.subject?.[".tag"] === "user"
          ? owner.subject.display_name
          : undefined) ?? "",
      );
    }
    return undefined;
  }, [owner]);

  const createdBy = useMemo(() => {
    if (isDashWelcomeStack) {
      return i18n.t("created_by_dropbox_dash");
    }
    return i18n.t("created_by", { name: ownerDisplayName });
  }, [isDashWelcomeStack, ownerDisplayName]);

  if (isStackChatEnabled) {
    return currentAccount ? (
      <Box display="flex" marginBottom="8" paddingX="8">
        <DigTooltip title={createdBy}>
          <StylelessButton>
            <Avatar
              size="small"
              src={
                owner?.subject?.[".tag"] === "user"
                  ? owner.subject.profile_photo_url
                  : currentAccount.profile_photo_url
              }
            >
              {isDashWelcomeStack ? "DD" : ownerInitials}
            </Avatar>
          </StylelessButton>
        </DigTooltip>
        {members.length > 0 && (
          <div style={{ paddingLeft: 4 }}>
            <DashFacepile members={members} size="small" />
          </div>
        )}
      </Box>
    ) : null;
  }

  if (!augustRevisionEnabled) {
    return (
      <div
        className={classnames(styles.defaultPadding, styles.membersContainer)}
      >
        <DashFacepile members={members} size="small" />
        <Text color="faint" size="small" className={styles.memberText}>
          {memberTextForStack(stack, currentAccount?.email ?? "")}
        </Text>
        <IconButton variant="transparent" onClick={onShareSettingsClick}>
          <UIIcon src={SettingsLine} color="var(--dig-color__text__subtle)" />
        </IconButton>
      </div>
    );
  }

  return null;
}
